import { resolveShareImageAlt, resolveShareImagePath } from './ogShare.js';

/** Absolute site origin for canonicals, sitemap, and JSON-LD. */
export const SITE_ORIGIN = 'https://loomihomepatios.ca';

let canonicalLinkEl = null;

/** Content URLs use a trailing slash; static files keep their extension. */
export function canonicalizePath(pathname = '/') {
  if (!pathname || pathname === '/') return '/';
  let p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  // Guard against sentence punctuation stuck to paths (e.g. "/guide.")
  p = p.replace(/[.,;:!?]+$/u, '');
  if (!p || p === '/') return '/';
  if (/\.[a-z0-9]{2,8}$/i.test(p)) return p;
  return p.endsWith('/') ? p : `${p}/`;
}

export function absoluteUrl(pathname) {
  if (!pathname) return `${SITE_ORIGIN}/`;
  if (/^https?:\/\//i.test(pathname)) return pathname;
  const path = canonicalizePath(pathname);
  return `${SITE_ORIGIN}${path}`;
}

export function setCanonicalPath(pathname) {
  if (typeof document === 'undefined') return;
  const href = absoluteUrl(pathname === '/' ? '/' : pathname);

  if (!canonicalLinkEl) {
    canonicalLinkEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalLinkEl) {
      canonicalLinkEl = document.createElement('link');
      canonicalLinkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLinkEl);
    }
  }
  canonicalLinkEl.setAttribute('href', href);
}

let hreflangEnCaEl = null;
let hreflangDefaultEl = null;
let llmsAlternateEl = null;

function upsertLinkRel(rel, attrs) {
  if (typeof document === 'undefined') return;
  const selector = Object.entries(attrs)
    .map(([key, value]) => `[${key}="${value}"]`)
    .join('');
  let el = document.querySelector(`link[rel="${rel}"]${selector}`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    document.head.appendChild(el);
  }
  return el;
}

function upsertLlmsAlternateLink() {
  if (typeof document === 'undefined') return;
  if (!llmsAlternateEl) {
    llmsAlternateEl = document.querySelector('link[rel="alternate"][type="text/plain"][href="/llms.txt"]');
    if (!llmsAlternateEl) {
      llmsAlternateEl = upsertLinkRel('alternate', {
        type: 'text/plain',
        href: '/llms.txt',
        title: 'LLM-readable site summary',
      });
    }
  }
}

function upsertHreflangAlternates(href) {
  if (typeof document === 'undefined') return;
  if (!hreflangEnCaEl) {
    hreflangEnCaEl = document.querySelector('link[rel="alternate"][hreflang="en-CA"]');
    if (!hreflangEnCaEl) {
      hreflangEnCaEl = document.createElement('link');
      hreflangEnCaEl.setAttribute('rel', 'alternate');
      hreflangEnCaEl.setAttribute('hreflang', 'en-CA');
      document.head.appendChild(hreflangEnCaEl);
    }
  }
  if (!hreflangDefaultEl) {
    hreflangDefaultEl = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (!hreflangDefaultEl) {
      hreflangDefaultEl = document.createElement('link');
      hreflangDefaultEl.setAttribute('rel', 'alternate');
      hreflangDefaultEl.setAttribute('hreflang', 'x-default');
      document.head.appendChild(hreflangDefaultEl);
    }
  }
  hreflangEnCaEl.setAttribute('href', href);
  hreflangDefaultEl.setAttribute('href', href);
}

function upsertMetaByName(name, content) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
}

function upsertMetaProperty(property, content) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content || '');
}

/** Keep meta descriptions within common SERP snippet limits. */
export function truncateMetaDescription(text, maxLen = 160) {
  const value = (text || '').trim();
  if (value.length <= maxLen) return value;
  const cut = value.slice(0, maxLen - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = lastSpace > 80 ? cut.slice(0, lastSpace) : cut;
  return `${trimmed.trim()}…`;
}

/** Per-route title, description, canonical, robots, and social tags for SPA SEO. */
export function setPageMeta({ title, description, path = '/', robots = 'index,follow', image, imageAlt } = {}) {
  if (typeof document === 'undefined') return;

  const pageTitle = title || 'LoomiHome Patios';
  const pageDescription = truncateMetaDescription(description);
  const pagePath = path || '/';
  const pageUrl = absoluteUrl(pagePath);
  const shareImagePath = resolveShareImagePath({ path: pagePath, heroImage: image });
  const shareImage = absoluteUrl(shareImagePath);
  const shareImageAlt = resolveShareImageAlt({ title: imageAlt || pageTitle, path: pagePath });

  document.title = pageTitle;
  upsertMetaByName('description', pageDescription);
  upsertMetaByName('robots', robots);
  setCanonicalPath(pagePath);
  upsertHreflangAlternates(pageUrl);
  upsertLlmsAlternateLink();
  upsertMetaByName('geo.region', 'CA-BC');
  upsertMetaByName('geo.placename', 'Metro Vancouver');
  upsertMetaByName('geo.position', '49.2488;-122.9805');
  upsertMetaByName('ICBM', '49.2488, -122.9805');

  upsertMetaProperty('og:type', 'website');
  upsertMetaProperty('og:site_name', 'LoomiHome Patios');
  upsertMetaProperty('og:title', pageTitle);
  upsertMetaProperty('og:description', pageDescription);
  upsertMetaProperty('og:url', pageUrl);
  upsertMetaProperty('og:image', shareImage);
  upsertMetaProperty('og:image:alt', shareImageAlt);
  upsertMetaProperty('og:locale', 'en_CA');
  upsertMetaProperty('og:image:width', '1200');
  upsertMetaProperty('og:image:height', '630');

  upsertMetaByName('twitter:card', 'summary_large_image');
  upsertMetaByName('twitter:title', pageTitle);
  upsertMetaByName('twitter:description', pageDescription);
  upsertMetaByName('twitter:image', shareImage);
  upsertMetaByName('twitter:image:alt', shareImageAlt);
}

const JSONLD_ID = 'loomihome-jsonld';

export function removeJsonLd() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(JSONLD_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/**
 * @param {object|object[]} graph - single schema object or @graph array
 */
export function injectJsonLd(graph) {
  if (typeof document === 'undefined') return;
  removeJsonLd();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSONLD_ID;
  const payload = Array.isArray(graph)
    ? { '@context': 'https://schema.org', '@graph': graph }
    : graph;
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
}

export function faqPageNode(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

export function webSiteNode() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE_ORIGIN}/#website`,
    name: 'LoomiHome Patios',
    url: SITE_ORIGIN,
    publisher: { '@id': `${SITE_ORIGIN}/#business` },
    inLanguage: 'en-CA',
  };
}

/** Node for @graph (no duplicate @context on each item). */
export function localBusinessNode() {
  return {
    '@type': 'HomeAndConstructionBusiness',
    '@id': `${SITE_ORIGIN}/#business`,
    name: 'LoomiHome Patios',
    description:
      'Patio covers and sunrooms in Vancouver and the Lower Mainland, backed by a team with 10+ years of combined experience and hundreds of completed projects. Design your patio cover online and see the price instantly with the free Design & Price It Yourself tool (https://loomihomepatios.ca/instant-quote/), then book a free on-site measurement for formal pricing. Burnaby showroom.',
    slogan: '10+ years of combined experience, hundreds of patio cover and sunroom projects across the Lower Mainland.',
    foundingDate: '2025',
    url: SITE_ORIGIN,
    image: `${SITE_ORIGIN}/house/Aluminum/aluminum-hero.png`,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 49.2488,
      longitude: -122.9805,
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Burnaby',
      addressRegion: 'BC',
      addressCountry: 'CA',
    },
    knowsAbout: [
      'Aluminum patio covers',
      'Glass patio covers',
      'Skyline combo patio covers',
      'Sunroom enclosures',
      'Aluminum carport covers',
      'Patio cover installation',
      'Metro Vancouver patio covers',
      'Online patio cover design and instant quote tool',
      'Patio cover cost calculator',
      'Patio cover showroom in Burnaby',
    ],
    areaServed: [
      { '@type': 'City', name: 'Vancouver' },
      { '@type': 'City', name: 'Burnaby' },
      { '@type': 'City', name: 'Richmond' },
      { '@type': 'City', name: 'Surrey' },
      { '@type': 'City', name: 'Delta' },
      { '@type': 'City', name: 'Coquitlam' },
      { '@type': 'City', name: 'Port Coquitlam' },
      { '@type': 'City', name: 'Port Moody' },
      { '@type': 'City', name: 'Langley' },
      { '@type': 'City', name: 'North Vancouver' },
      { '@type': 'City', name: 'West Vancouver' },
      { '@type': 'City', name: 'New Westminster' },
      { '@type': 'City', name: 'Maple Ridge' },
      { '@type': 'City', name: 'Pitt Meadows' },
      { '@type': 'City', name: 'Abbotsford' },
      { '@type': 'City', name: 'White Rock' },
      { '@type': 'AdministrativeArea', name: 'Lower Mainland' },
      { '@type': 'AdministrativeArea', name: 'Metro Vancouver' },
    ],
    priceRange: '$$',
    email: 'mailto:info@loomihomepatios.ca',
    telephone: '+1-778-743-5577',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'info@loomihomepatios.ca',
      telephone: '+1-778-743-5577',
      areaServed: 'CA-BC',
      availableLanguage: ['en'],
    },
    makesOffer: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Aluminum patio covers' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Glass patio covers' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Skyline combo patio covers' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Sunrooms' } },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Online patio cover design and instant quote tool',
          url: 'https://loomihomepatios.ca/instant-quote/',
        },
      },
    ],
  };
}

export function webPageNode(page) {
  return {
    '@type': 'WebPage',
    '@id': `${absoluteUrl(page.path)}#webpage`,
    url: absoluteUrl(page.path),
    name: page.metaTitle || page.h1,
    description: page.metaDescription || page.intro,
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_ORIGIN}/#website`,
      name: 'LoomiHome Patios',
      url: SITE_ORIGIN,
    },
    about: { '@id': `${SITE_ORIGIN}/#business` },
  };
}

export function articleNode(page) {
  if (!page || !page.path) return null;
  const image = page.heroImage ? absoluteUrl(page.heroImage) : `${SITE_ORIGIN}/house/Aluminum/aluminum-hero.png`;
  const today = new Date().toISOString().slice(0, 10);
  return {
    '@type': 'Article',
    '@id': `${absoluteUrl(page.path)}#article`,
    headline: page.h1,
    description: page.metaDescription || page.intro,
    image,
    datePublished: page.datePublished || '2026-06-01',
    dateModified: page.dateModified || today,
    mainEntityOfPage: { '@id': `${absoluteUrl(page.path)}#webpage` },
    author: { '@id': `${SITE_ORIGIN}/#business` },
    publisher: { '@id': `${SITE_ORIGIN}/#business` },
    inLanguage: 'en-CA',
  };
}

export function serviceNode(page) {
  if (!page || !page.serviceType) return null;
  const node = {
    '@type': 'Service',
    '@id': `${absoluteUrl(page.path)}#service`,
    name: page.h1,
    description: page.metaDescription || page.intro,
    url: absoluteUrl(page.path),
    serviceType: page.serviceType,
    provider: { '@id': `${SITE_ORIGIN}/#business` },
    areaServed: {
      '@type': page.areaServed && page.areaServed.includes(',') ? 'City' : 'AdministrativeArea',
      name: page.areaServed || 'Lower Mainland, British Columbia',
    },
  };
  if (page.heroImage) node.image = absoluteUrl(page.heroImage);
  return node;
}

export function breadcrumbNode(items) {
  if (!items || items.length < 2) return null;
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
